import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Laugh, RotateCcw, Image as ImageIcon, Type } from 'lucide-react';

type FontFamily = 'Impact' | 'Arial' | 'Comic Sans MS';
type OutputFormat = 'png' | 'jpg';

interface MemeTemplate {
  label: string;
  color: string;
  description: string;
}

const TEMPLATES: MemeTemplate[] = [
  { label: 'Drake', color: '#f59e0b', description: 'Approve / Disapprove' },
  { label: 'Distracted BF', color: '#ef4444', description: 'Looking away meme' },
  { label: 'Two Buttons', color: '#3b82f6', description: 'Hard choice meme' },
  { label: 'Change My Mind', color: '#10b981', description: 'Debate format' },
  { label: 'Expanding Brain', color: '#8b5cf6', description: 'Levels of intellect' },
];

const FONTS: { value: FontFamily; label: string }[] = [
  { value: 'Impact', label: 'Impact (Classic)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Comic Sans MS', label: 'Comic Sans' },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MemeGenerator: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [topText, setTopText] = useState('');
  const [bottomText, setBottomText] = useState('');
  const [topFontSize, setTopFontSize] = useState(48);
  const [bottomFontSize, setBottomFontSize] = useState(48);
  const [fontFamily, setFontFamily] = useState<FontFamily>('Impact');
  const [textColor, setTextColor] = useState('#ffffff');
  const [outlineColor, setOutlineColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [allCaps, setAllCaps] = useState(true);
  const [topPadding, setTopPadding] = useState(10);
  const [bottomPadding, setBottomPadding] = useState(10);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const wrapText = useCallback((ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
    return lines;
  }, []);

  const drawMeme = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img, 0, 0);

    // Scale font size relative to image width
    const scale = w / 800;
    const drawText = (text: string, fontSize: number, yPosition: number, isTop: boolean) => {
      if (!text.trim()) return;

      const displayText = allCaps ? text.toUpperCase() : text;
      const scaledFontSize = fontSize * scale;
      ctx.font = `bold ${scaledFontSize}px "${fontFamily}", Impact, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = isTop ? 'top' : 'bottom';

      const maxWidth = w * 0.9;
      const lines = wrapText(ctx, displayText, maxWidth);
      const lineHeight = scaledFontSize * 1.2;

      lines.forEach((line, index) => {
        let y: number;
        if (isTop) {
          y = yPosition + index * lineHeight;
        } else {
          y = yPosition - (lines.length - 1 - index) * lineHeight;
        }

        // Outline
        ctx.strokeStyle = outlineColor;
        ctx.lineWidth = strokeWidth * scale;
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
        ctx.strokeText(line, w / 2, y);

        // Fill
        ctx.fillStyle = textColor;
        ctx.fillText(line, w / 2, y);
      });
    };

    // Draw top text
    if (topText) {
      drawText(topText, topFontSize, topPadding * scale, true);
    }

    // Draw bottom text
    if (bottomText) {
      drawText(bottomText, bottomFontSize, h - bottomPadding * scale, false);
    }
  }, [topText, bottomText, topFontSize, bottomFontSize, fontFamily, textColor, outlineColor, strokeWidth, allCaps, topPadding, bottomPadding, wrapText]);

  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    drawMeme(previewCanvasRef.current, imgElement);
  }, [imgElement, drawMeme]);

  const handleDownload = useCallback(() => {
    if (!imgElement) return;
    const canvas = document.createElement('canvas');
    drawMeme(canvas, imgElement);
    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;
    const dataUrl = canvas.toDataURL(mimeType, quality);
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'meme';
    link.download = `${baseName}-meme.${outputFormat === 'jpg' ? 'jpg' : 'png'}`;
    link.href = dataUrl;
    link.click();
  }, [imgElement, drawMeme, outputFormat, jpgQuality, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setImgElement(null);
    setImageDimensions(null);
    setTopText('');
    setBottomText('');
    setTopFontSize(48);
    setBottomFontSize(48);
    setFontFamily('Impact');
    setTextColor('#ffffff');
    setOutlineColor('#000000');
    setStrokeWidth(3);
    setAllCaps(true);
    setTopPadding(10);
    setBottomPadding(10);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Laugh className="w-6 h-6 text-orange-600" />
          <h1 className="text-2xl font-bold text-gray-900">Meme Generator</h1>
        </div>
        <p className="text-gray-600">Create classic memes with customizable text, fonts, and styling.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div className="space-y-6">
          {/* Template suggestions */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Popular Templates (upload your own image)</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {TEMPLATES.map((t) => (
                <div
                  key={t.label}
                  className="border border-gray-200 rounded-lg p-3 text-center cursor-pointer hover:border-orange-300 hover:bg-orange-50/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div
                    className="w-full aspect-square rounded-lg mb-2 flex items-center justify-center text-white font-bold text-xs"
                    style={{ backgroundColor: t.color }}
                  >
                    {t.label}
                  </div>
                  <div className="text-xs text-gray-500">{t.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Upload area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors"
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
            <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Controls */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Meme Text</h2>
                <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                  <RotateCcw className="w-4 h-4" /> Reset
                </button>
              </div>

              {/* Top text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Top Text</label>
                <input
                  type="text"
                  value={topText}
                  onChange={(e) => setTopText(e.target.value)}
                  placeholder="Enter top text..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <div className="flex items-center gap-2 mt-1">
                  <label className="text-xs text-gray-500">Size: {topFontSize}px</label>
                  <input
                    type="range"
                    min={16}
                    max={120}
                    value={topFontSize}
                    onChange={(e) => setTopFontSize(Number(e.target.value))}
                    className="flex-1 accent-orange-600"
                  />
                </div>
              </div>

              {/* Bottom text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bottom Text</label>
                <input
                  type="text"
                  value={bottomText}
                  onChange={(e) => setBottomText(e.target.value)}
                  placeholder="Enter bottom text..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <div className="flex items-center gap-2 mt-1">
                  <label className="text-xs text-gray-500">Size: {bottomFontSize}px</label>
                  <input
                    type="range"
                    min={16}
                    max={120}
                    value={bottomFontSize}
                    onChange={(e) => setBottomFontSize(Number(e.target.value))}
                    className="flex-1 accent-orange-600"
                  />
                </div>
              </div>

              {/* Text styling */}
              <div className="border-t border-gray-100 pt-4 space-y-4">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-1">
                  <Type className="w-4 h-4" /> Text Styling
                </h3>

                {/* Font */}
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Font</label>
                  <select
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value as FontFamily)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    {FONTS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>

                {/* Colors */}
                <div className="flex gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Text Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-8 h-8 rounded border border-gray-300 cursor-pointer" />
                      <span className="text-xs font-mono text-gray-500">{textColor}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Outline Color</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={outlineColor} onChange={(e) => setOutlineColor(e.target.value)} className="w-8 h-8 rounded border border-gray-300 cursor-pointer" />
                      <span className="text-xs font-mono text-gray-500">{outlineColor}</span>
                    </div>
                  </div>
                </div>

                {/* Stroke width */}
                <div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Outline Width</span>
                    <span className="font-mono text-orange-600">{strokeWidth}px</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    value={strokeWidth}
                    onChange={(e) => setStrokeWidth(Number(e.target.value))}
                    className="w-full accent-orange-600"
                  />
                </div>

                {/* All caps */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="allCaps"
                    checked={allCaps}
                    onChange={(e) => setAllCaps(e.target.checked)}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <label htmlFor="allCaps" className="text-sm text-gray-700">ALL CAPS</label>
                </div>

                {/* Text position */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Top Padding</span>
                      <span className="font-mono text-orange-600">{topPadding}px</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={topPadding}
                      onChange={(e) => setTopPadding(Number(e.target.value))}
                      className="w-full accent-orange-600"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Bottom Padding</span>
                      <span className="font-mono text-orange-600">{bottomPadding}px</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={bottomPadding}
                      onChange={(e) => setBottomPadding(Number(e.target.value))}
                      className="w-full accent-orange-600"
                    />
                  </div>
                </div>
              </div>

              {/* Output format */}
              <div className="flex flex-wrap gap-4 items-end border-t border-gray-100 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="png">PNG</option>
                    <option value="jpg">JPG</option>
                  </select>
                </div>
                {outputFormat === 'jpg' && (
                  <div className="flex-1 min-w-[150px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quality: {jpgQuality}%</label>
                    <input type="range" min={10} max={100} value={jpgQuality} onChange={(e) => setJpgQuality(Number(e.target.value))} className="w-full accent-orange-600" />
                  </div>
                )}
              </div>
            </div>

            {/* Preview */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Live Preview</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <canvas ref={previewCanvasRef} className="w-full h-auto" />
              </div>

              {/* Image info */}
              {imageDimensions && file && (
                <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-3">
                  <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
                  <span>{formatFileSize(file.size)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Download */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
            >
              <Download className="w-5 h-5" /> Download Meme
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemeGenerator;
